import { observer } from "mobx-react-lite";
import { ProposalPricingWithServiceNames, useProposalSectionPricingStore } from "./ProposalSectionPricingStore";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { ServiceRatePeriodSuffix, serviceRateTypeEnumsLabels } from "@gap/backend/app-services/services/models/ServiceRateTypesDto";
import ProposalSectionPricingSchemeSelector from "./ProposalSectionPricingSchemeSelector";
import { useTheme } from "theme/useTheme";
import { useProposalsViewProposalStore } from "../../ProposalsViewProposalStore";
import { doesServiceRateTypeHaveEstimatedLineItems, getServiceRateTotalText } from "features/proposals/proposalsUtils";
import { notNull } from "@gap/utils/utils";

export default observer(function PricingCategoryCard({ pricingItem, showPreviewHints }: PricingCategoryCardProps) {
   const proposalSectionPricingStore = useProposalSectionPricingStore()
   const theme = useTheme()
   const border = `1px solid ${theme.palette.gap.primary['200']}`;
   const { formatCurrency: format, taxDescription } = proposalSectionPricingStore
   const { paymentPreferences } = useProposalsViewProposalStore()
   const selectedPaymentMethod = paymentPreferences[pricingItem.serviceRateType]

   const totals = selectedPaymentMethod && getServiceRateTotalText(pricingItem, selectedPaymentMethod, notNull(proposalSectionPricingStore.sp.proposalsViewStore.advisorOrg.currencyCode))

   return (
      <Stack sx={{
         border,
         borderRadius: '24px',
         overflow: 'hidden',
         '> *': {
            padding: 2,
         },
         breakInside: 'avoid-page',
      }}>
         <Stack sx={{
            backgroundColor: theme.palette.gap.primary['50'],
            borderBottom: border,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
         }}>
            <Stack sx={{ flexGrow: 1, paddingY: '5px' }}>
               <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{serviceRateTypeEnumsLabels[pricingItem.serviceRateType]}</Typography>
            </Stack>
            <Stack sx={{ textAlign: { xs: 'center', sm: 'right' }, alignItems: 'end' }}>
               {
                  totals?.totalText && <Stack sx={{ flexDirection: 'row' }}>
                     <Stack>
                        <Stack sx={{ flexDirection: 'row', gap: 1 }}>
                           {totals?.preDiscountText && <Typography sx={{
                              color: 'secondary.light',
                              textDecoration: 'line-through',
                              whiteSpace: 'nowrap',
                           }}>{totals?.preDiscountText}</Typography>}
                           <Typography variant="body1" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{totals.totalText}</Typography>
                        </Stack>
                        <Typography variant="caption">Excl. {taxDescription}</Typography>
                     </Stack>
                     {totals.estimateMarker && <Typography variant="body1">{totals.estimateMarker}</Typography>}
                  </Stack>
               }
            </Stack>
         </Stack>
         <Stack sx={{ gap: 6, paddingY: 5 }}>
            { // @ts-expect-error – for some reason, tsc thinks serviceName doesn't exist. This may be a bug to do with Zod because this works ¯\_(ツ)_/¯
               pricingItem.services.map(({ serviceName, servicePrice, serviceUnits, serviceAgreementType }, index) => <Stack key={index}>
                  <Stack sx={{ flexDirection: 'row', gap: 1 }}>
                     <Typography>
                        {serviceName}
                     </Typography>
                     <Stack sx={{
                        flexGrow: 1,
                        borderBottom: { xs: 'none', sm: `1px dashed ${theme.palette.gap.primary['200']}` },
                     }}></Stack>
                     <Stack sx={{ textAlign: 'right' }}>
                        <Typography>
                           {format(servicePrice)}
                           {ServiceRatePeriodSuffix[pricingItem.serviceRateType]}
                           {serviceAgreementType === 'estimate' && '*'}
                        </Typography>
                     </Stack>
                  </Stack>
                  {(typeof serviceUnits === 'undefined' || !['Per session', 'Per unit'].includes(pricingItem.serviceRateType))
                     ? undefined
                     : <Typography
                        variant="caption"
                        sx={{ color: 'secondary.light', textAlign: 'right' }}
                     >Quantity: {serviceUnits}
                     </Typography>}
               </Stack>)}
         </Stack>
         <ProposalSectionPricingSchemeSelector {...{ pricingItem, showPreviewHints }} />
         {doesServiceRateTypeHaveEstimatedLineItems(pricingItem) && <Stack sx={{ borderTop: border }}>
            <Typography variant="caption" sx={{ color: theme.palette.gap.primary['600'] }}>
               *Estimated price only. If any unanticipated issues arise that increase our internal costs during the course of work,
               we will contact you prior to continuing.
            </Typography>
         </Stack>}
      </Stack>
   )
})

interface PricingCategoryCardProps {
   pricingItem: ProposalPricingWithServiceNames,
   /** There are some nuances if this is an advisor previewing their proposal */
   showPreviewHints?: boolean,
}
